import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { GroupOffersBy, TOffer, TPartner } from 'types/offers-temporary';
import { Button, Input, Modal, SelectPicker, Whisper, Popover } from 'rsuite';
import Particles from "react-tsparticles";
import { Container, Engine } from "tsparticles-engine";
import { loadFull } from "tsparticles";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSearch, faMobileAlt, faCopy } from '@fortawesome/free-solid-svg-icons';
import ReactCountryFlag from 'react-country-flag';
import AllOffersList from './all/all-offers-list';
import OffersList from './offers/offers-list';
import { resolveSectionTitle } from './offers/section/section-title';
import AddSectionToListModal from './modals/add-section-to-list-modal';
import SectionsManager from './sections/sections-manager';
import './_list.scss';
import SplitLayout from './split/split-layout';
import { PanelService } from '../../../network/panel/network';
import type { ISectionsList, IAppOffersSection } from '@bprotsyk/aso-core/lib/offers/list';
import type { IOffersSection } from '@bprotsyk/aso-core/lib/offers/section';
import type { IApp } from '@bprotsyk/aso-core';
import { countryNames, getCountryFlag } from '../../../constants/countries';
import { OfferCrudModal } from 'ui/modals/offer/offer-crud';
import { createLogger } from '../../../utils/logger';
import { getCompleteOfferPool } from './offer-pool';
import {
  buildSectionsListSavePayload,
  createSectionsListDraft,
  renameSectionsListDraft,
  SectionsListItem
} from './list-draft';
import {
  applySectionsListFallbackToApps,
  selectSectionsListFallbackId,
} from './list-delete';
import panelHttp from '../../../network/panel/http';
import { API_BASE_URL } from '../../../config/runtime';
import type { TOfferwallType } from '../../../types/offerwall-type';

const logger = createLogger('offer-list');
const PDL_OFFERWALL_TYPE: TOfferwallType = 'MFO';

function normalizeOfferwallPreviewOrigin(value?: string | null): string {
  return String(value || API_BASE_URL).replace(/\/+$/, '');
}

const OFFERWALL_PREVIEW_ORIGIN = normalizeOfferwallPreviewOrigin(
  process.env.REACT_APP_OFFERWALL_PREVIEW_ORIGIN ||
  process.env.REACT_APP_API_URL ||
  panelHttp.defaults.baseURL
);

function normalizeOfferwallPreviewHtml(html: string): string {
  if (!html) return html;
  if (/<base\s/i.test(html)) return html;
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1><base href="${OFFERWALL_PREVIEW_ORIGIN}/">`);
  }
  return `<base href="${OFFERWALL_PREVIEW_ORIGIN}/">${html}`;
}

async function fetchOfferwallPreviewHtml(
  version: TOfferwallType,
  geo: string,
  sectionsListContent: IAppOffersSection[]
): Promise<string> {
  const response = await fetch(`${OFFERWALL_PREVIEW_ORIGIN}/test-offerwall`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      version,
      geo,
      sectionsListContent
    })
  });
  const body = await response.text();

  if (!response.ok) {
    let detail = body.trim();
    try {
      const parsed = JSON.parse(body);
      detail = parsed?.error || parsed?.message || detail;
    } catch (error) {
      // Keep the raw backend response as the visible preview error.
    }
    throw new Error(`Preview HTTP ${response.status}: ${detail || response.statusText}`);
  }

  if (!body.trim()) {
    throw new Error('Preview response is empty.');
  }

  return normalizeOfferwallPreviewHtml(body);
}

interface OfferListContainerProps {
  onSectionsListChange?: (newSectionsList: ISectionsList) => void;
}

type ListItem = SectionsListItem;

const OfferListContainer: React.FC<OfferListContainerProps> = ({ onSectionsListChange }) => {
  const [selectedOffers, setSelectedOffers] = useState<TOffer[]>([]);
  const [partners, setPartners] = useState<Record<string, TPartner>>({});
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<number>(1);
  const [draggedOffer, setDraggedOffer] = useState<TOffer | null>(null);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [lastExpandedSection, setLastExpandedSection] = useState<number>(1);
  const [sectionsList, setSectionsList] = useState<ISectionsList>({
    id: 1,
    name: 'Sections List 1',
    content: [
      {
        sectionId: 1,
        offerStatesOrdered: {}
      },
      {
        sectionId: 2,
        offerStatesOrdered: {}
      }
    ]
  });

  // New state for hierarchical view
  const [lists, setLists] = useState<ListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingListName, setIsEditingListName] = useState(false);
  const [editingListName, setEditingListName] = useState('');
  const [activeTab, setActiveTab] = useState<'offerwall' | 'direct' | 'sections'>('offerwall');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState<ListItem | null>(null);
  const [deletingListId, setDeletingListId] = useState<number | null>(null);
  const [deleteHoldTimer, setDeleteHoldTimer] = useState<NodeJS.Timeout | null>(null);
  const [isHoldingDelete, setIsHoldingDelete] = useState<number | null>(null);
  const [holdProgress, setHoldProgress] = useState<Record<number, number>>({});
  const [isRemoving, setIsRemoving] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalSectionsList, setOriginalSectionsList] = useState<ISectionsList | null>(null);
  const [originalListName, setOriginalListName] = useState<string>('');
  const [isCreatingNewList, setIsCreatingNewList] = useState(false);
  const [newListData, setNewListData] = useState<ListItem | null>(null);
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const holdStartTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const [apps, setApps] = useState<IApp[]>([]);
  const [listAppCounts, setListAppCounts] = useState<Record<number, number>>({});
  const [listAppIds, setListAppIds] = useState<Record<number, number[]>>({});
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isPreviewModalClosing, setIsPreviewModalClosing] = useState(false);
  const [previewGeo, setPreviewGeo] = useState<string>('UA');
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);
  const [editingOffer, setEditingOffer] = useState<TOffer | null>(null);
  const [sectionTitles, setSectionTitles] = useState<Record<number, string>>({});

  const closePreviewModal = useCallback(() => {
    setIsPreviewModalClosing(true);
    setTimeout(() => {
      setIsPreviewModalOpen(false);
      setIsPreviewModalClosing(false);
      setPreviewHtml('');
      setPreviewError(null);
    }, 200);
  }, []);

  // Handle ESC key to close preview modal
  useEffect(() => {
    if (!isPreviewModalOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closePreviewModal();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPreviewModalOpen, closePreviewModal]);

  // Fetch preview HTML when modal is open or when sectionsList/geo/version changes
  useEffect(() => {
    if (!isPreviewModalOpen || !sectionsList?.content) return;
    
    const fetchPreview = async () => {
      setIsLoadingPreview(true);
      setPreviewError(null);
      try {
        const html = await fetchOfferwallPreviewHtml(PDL_OFFERWALL_TYPE, previewGeo, sectionsList.content);
        setPreviewHtml(html);
      } catch (error) {
        logger.error('Failed to fetch preview', error);
        setPreviewHtml('');
        setPreviewError(error instanceof Error ? error.message : 'Preview failed to load.');
      } finally {
        setIsLoadingPreview(false);
      }
    };
    
    fetchPreview();
  }, [isPreviewModalOpen, sectionsList?.content, previewGeo]);

  // Compute available geos from current sectionsList (realtime, not saved)
  const availableGeos = React.useMemo(() => {
    const geoSet = new Set<string>();
    if (sectionsList?.content) {
      sectionsList.content.forEach(section => {
        if (section.offerStatesOrdered) {
          Object.keys(section.offerStatesOrdered).forEach(geo => {
            const offers = section.offerStatesOrdered[geo];
            if (offers && offers.length > 0 && offers.some(o => !o.hidden)) {
              geoSet.add(geo);
            }
          });
        }
      });
    }
    return geoSet;
  }, [sectionsList]);

  const geoPickerData = React.useMemo(() => {
    return Object.keys(countryNames)
      .filter(code => availableGeos.has(code))
      .map((code) => ({
        label: (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>{getCountryFlag(code)}</span>
            <span style={{ flex: 1 }}>{countryNames[code]}</span>
            <span style={{ fontWeight: 700, color: '#888' }}>{code}</span>
          </span>
        ),
        value: code,
        searchLabel: `${countryNames[code]} ${code}`
      }));
  }, [availableGeos]);

  // Reset delete state when switching tabs or views
  useEffect(() => {
    resetDeleteState();
  }, [activeTab, isExpanded]);

  useEffect(() => {
    const fetchApps = async () => {
      try {
        const allApps = await PanelService.getApps(undefined, false);
        setApps(allApps);
        const counts: Record<number, number> = {};
        const appIds: Record<number, number[]> = {};
        allApps.forEach(app => {
          if (app.offerwallParams?.sectionsListId) {
            const listId = app.offerwallParams.sectionsListId;
            counts[listId] = (counts[listId] || 0) + 1;
            if (!appIds[listId]) appIds[listId] = [];
            appIds[listId].push(app.id);
          }
        });
        setListAppCounts(counts);
        setListAppIds(appIds);
      } catch (error) {
        logger.error('Failed to fetch apps', error);
      }
    };
    fetchApps();
  }, []);

  const checkForChanges = useCallback(() => {
    if (!selectedListId) {
      setHasUnsavedChanges(false);
      return;
    }

    // For new lists, always consider them as having changes
    if (isCreatingNewList) {
      setHasUnsavedChanges(true);
      return;
    }

    if (!originalSectionsList) {
      setHasUnsavedChanges(false);
      return;
    }

    const selectedList = lists.find(list => list.id === selectedListId);
    if (!selectedList) {
      setHasUnsavedChanges(false);
      return;
    }

    // Check if name has changed (either in editing mode or saved)
    const nameChanged = isEditingListName 
      ? editingListName !== originalListName
      : selectedList.name !== originalListName;
    
    // Check if sections list has changed (deep comparison, excluding offerwallType)
    const sectionsListWithoutType = { ...sectionsList };
    delete (sectionsListWithoutType as any)['offerwallType'];
    const originalSectionsListWithoutType = { ...originalSectionsList };
    delete (originalSectionsListWithoutType as any)['offerwallType'];
    const sectionsChanged = JSON.stringify(sectionsListWithoutType) !== JSON.stringify(originalSectionsListWithoutType);
    
    setHasUnsavedChanges(nameChanged || sectionsChanged);
  }, [originalSectionsList, originalListName, selectedListId, sectionsList, lists, isEditingListName, editingListName, isCreatingNewList]);

  // Check for changes whenever relevant state changes
  useEffect(() => {
    checkForChanges();
  }, [checkForChanges]);

  // Warn before closing browser tab if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'У вас є незбережені зміни. Ви впевнені, що хочете вийти?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Log sections list changes
  useEffect(() => {
    logger.debug('Sections List Updated', sectionsList);
    logger.debug('Selected Offers', selectedOffers);
  }, [sectionsList, selectedOffers]);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await PanelService.getAllOffers(GroupOffersBy.PARTNERS, false, true);
        setPartners(Object.fromEntries(response.partners.map(p => [p.partnerId, p])));
        
        const allOffers = getCompleteOfferPool(response);
        setSelectedOffers(allOffers);
        logger.info('Fetched offers', allOffers);
      } catch (error) {
        logger.error('Error fetching partners', error);
      }
    };

    const fetchSectionsLists = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const sectionsLists = await PanelService.getSectionsLists();
        
        // Convert API response to ListItem format and ensure proper structure
        const listItems: ListItem[] = sectionsLists.map((list: any) => ({
          id: list.id,
          name: list.name,
          sectionsList: {
            ...list,
            content: (list.content || []).map((section: any) => ({
              ...section,
              offerStatesOrdered: section.offerStatesOrdered || {}
            }))
          },
        }));
        
        setLists(listItems);
      } catch (error) {
        logger.error('Error fetching sections lists', error);
        setError('Failed to load sections lists');
      } finally {
        setIsLoading(false);
      }
    };

    const fetchSectionTitles = async () => {
      try {
        const sections = await PanelService.getSections();
        setSectionTitles(Object.fromEntries(
          sections.map((section, index) => [
            section.id,
            resolveSectionTitle(section, index === 0 ? 'Default' : `Default ${index + 1}`),
          ])
        ));
      } catch (error) {
        logger.warn('Failed to fetch section titles; using stable fallbacks', error);
        setSectionTitles({});
      }
    };

    fetchPartners();
    fetchSectionsLists();
    fetchSectionTitles();
  }, []);

  const handleAddSection = () => {
    setIsAddSectionModalOpen(true);
  };

  const handleAddSectionSubmit = (sectionData: any) => {
    setSectionsList(prev => ({
      ...prev,
      content: [...prev.content, {
        sectionId: prev.content.length + 1,
        offerStatesOrdered: {}
      }]
    }));
    setIsAddSectionModalOpen(false);
  };

  const isOfferInAnySection = (offerId: number): boolean => {
    return sectionsList.content.some(section => 
      Object.values(section.offerStatesOrdered || {}).some(states =>
        (states || []).some(state => state.offerId === offerId)
      )
    );
  };

  const getOfferHiddenState = (offerId: number): boolean => {
    for (const section of sectionsList.content) {
      for (const states of Object.values(section.offerStatesOrdered || {})) {
        const state = (states || []).find(state => state.offerId === offerId);
        if (state) {
          return state.hidden;
        }
      }
    }
    return false;
  };

  const cleanupSection = (section: IAppOffersSection): IAppOffersSection => ({
    ...section,
    offerStatesOrdered: Object.fromEntries(
      Object.entries(section.offerStatesOrdered || {}).map(([key, states]) => [
        key,
        // Only filter out null/undefined offerIds, don't remove offers that might not be in selectedOffers yet
        // (e.g., during refresh). The offerId is the source of truth, not the selectedOffers array.
        (states || []).filter(state => state.offerId != null)
      ])
    )
  });

  const setSectionsListClean = (newState: ISectionsList | ((prev: ISectionsList) => ISectionsList)) => {
    if (typeof newState === 'function') {
      setSectionsList(prev => {
        const result = newState(prev);
        return {
          ...result,
          content: result.content.map(cleanupSection)
        };
      });
    } else {
      setSectionsList({
        ...newState,
        content: newState.content.map(cleanupSection)
      });
    }
  };

  const handleSectionExpand = (sectionId: number) => {
    if (sectionId !== expandedSection) {
      setExpandedSection(sectionId);
      onSectionsListChange?.(sectionsList);
    }
  };

  const handleSectionsListChange = (newSectionsList: ISectionsList) => {
    setSectionsList(newSectionsList);
    onSectionsListChange?.(newSectionsList);
    
    logger.debug('handleSectionsListChange', newSectionsList);
    // Update the selected list's sectionsList
    if (selectedListId) {
      setLists(prev => prev.map(list => 
        list.id === selectedListId 
          ? { ...list, sectionsList: newSectionsList }
          : list
      ));
    }
    
    // Trigger change detection immediately
    checkForChanges();
  };

  const handleDragStart = (offer: TOffer) => {
    if (!isReorderMode) {
      setDraggedOffer(offer);
    }
  };

  const handleEditOffer = (offer: TOffer) => {
    setEditingOffer(offer);
  };

  const refreshPreview = useCallback(async () => {
    if (!isPreviewModalOpen || !sectionsList?.content) return;
    
    setIsLoadingPreview(true);
    setPreviewError(null);
    try {
      const html = await fetchOfferwallPreviewHtml(PDL_OFFERWALL_TYPE, previewGeo, sectionsList.content);
      setPreviewHtml(html);
    } catch (error) {
      logger.error('Failed to refresh preview', error);
      setPreviewHtml('');
      setPreviewError(error instanceof Error ? error.message : 'Preview failed to load.');
    } finally {
      setIsLoadingPreview(false);
    }
  }, [isPreviewModalOpen, sectionsList?.content, previewGeo]);

  const handleOfferSaved = async () => {
    // Refresh the offers list
    try {
      const response = await PanelService.getAllOffers(GroupOffersBy.PARTNERS, false, true);
      setPartners(Object.fromEntries(response.partners.map(p => [p.partnerId, p])));
      const allOffers = getCompleteOfferPool(response);
      setSelectedOffers(allOffers);

      // Ensure sectionsList references are still valid - offers are keyed by ID, so this should be fine
      // But we need to make sure the sectionsList doesn't lose track of offers
      // The sectionsList uses offerId, not name, so it should persist correctly
      
      // Refresh preview if open (live update)
      if (isPreviewModalOpen) {
        await refreshPreview();
      }
    } catch (error) {
      logger.error('Error refreshing offers', error);
    }
    setEditingOffer(null);
  };

  const handleDragEnd = (result: DropResult) => {
    setDraggedOffer(null);
  };

  const handleAddOffer = (offerId: number, sectionId: number, insertIndex?: number) => {
    setSectionsList(prev => {
      return {
        ...prev,
        content: prev.content.map(section => {
          if (section.sectionId === sectionId) {
            const offer = selectedOffers.find(o => o.id === offerId);
            if (!offer) return section;
            
            const geo = offer.geo || 'other';
            const states = section.offerStatesOrdered[geo] || [];
            
            if (insertIndex !== undefined) {
              const newStates = [...states];
              newStates.splice(insertIndex, 0, { offerId, hidden: false });
              return {
                ...section,
                offerStatesOrdered: {
                  ...section.offerStatesOrdered,
                  [geo]: newStates
                }
              };
            } else {
              return {
                ...section,
                offerStatesOrdered: {
                  ...section.offerStatesOrdered,
                  [geo]: [...states, { offerId, hidden: false }]
                }
              };
            }
          }
          // Remove from other sections
          return {
            ...section,
            offerStatesOrdered: Object.fromEntries(
              Object.entries(section.offerStatesOrdered || {}).map(([key, states]) => [
                key,
                (states || []).filter(state => state.offerId !== offerId)
              ])
            )
          };
        })
      };
    });
  };

  const toggleReorderMode = () => {
    const newMode = !isReorderMode;
    if (draggedOffer) {
      setDraggedOffer(null);
    }
    
    if (newMode) {
      setLastExpandedSection(expandedSection);
      setExpandedSection(-1);
      setIsReorderMode(true);
    } else {
      // First expand the section
      handleSectionExpand(lastExpandedSection);
      // Then exit reorder mode in the next frame to allow animation
      requestAnimationFrame(() => {
        setIsReorderMode(false);
      });
    }
  };

  // New handlers for hierarchical view
  const handleCreateNewList = () => {
    const newList = createSectionsListDraft(lists);
    
    setNewListData(newList);
    setIsCreatingNewList(true);
    setSelectedListId(newList.id);
    setSectionsList(newList.sectionsList);
    setOriginalSectionsList(JSON.parse(JSON.stringify(newList.sectionsList))); // Deep copy
    setOriginalListName(newList.name);
    setExpandedSection(newList.sectionsList.content[0]?.sectionId || -1);
    setLastExpandedSection(newList.sectionsList.content[0]?.sectionId || -1);
    setIsExpanded(true);
    
    // Auto-start editing the name field
    setEditingListName(newList.name);
    setIsEditingListName(true);
    setSaveError(null);
    setHasUnsavedChanges(true);
  };

  const handleSelectList = (listId: number) => {
    const selectedList = lists.find(list => list.id === listId);
    if (selectedList) {
      setSelectedListId(listId);
      setIsExpanded(true);
      setSectionsList(selectedList.sectionsList);
      const firstSectionId = selectedList.sectionsList.content[0]?.sectionId || -1;
      setExpandedSection(firstSectionId);
      setLastExpandedSection(firstSectionId);
      setOriginalSectionsList(JSON.parse(JSON.stringify(selectedList.sectionsList))); // Deep copy
      setOriginalListName(selectedList.name);
      setHasUnsavedChanges(false);
    }
  };

  const handleBackToList = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm('У вас є незбережені зміни. Ви впевнені, що хочете вийти? Всі зміни буде втрачено.');
      if (!confirmLeave) {
        return;
      }
    }
    
    setIsExpanded(false);
    setSelectedListId(null);
    setIsEditingListName(false);
    setHasUnsavedChanges(false);
    
    // If we were creating a new list and didn't save it, clean up
    if (isCreatingNewList) {
      setIsCreatingNewList(false);
      setNewListData(null);
    }
  };

  const handleEditListName = () => {
    const selectedList = lists.find(list => list.id === selectedListId) || newListData;
    if (selectedList) {
      setEditingListName(selectedList.name);
      setIsEditingListName(true);
    }
  };

  const handleSaveListName = () => {
    if (editingListName.trim() && selectedListId) {
      const finalName = editingListName.trim();
      if (isCreatingNewList && newListData?.id === selectedListId) {
        setNewListData((draft) => draft ? renameSectionsListDraft(draft, finalName) : draft);
        setSectionsList((current) => ({ ...current, name: finalName }));
      } else {
        setLists(prev => prev.map(list =>
          list.id === selectedListId
            ? { ...list, name: finalName, sectionsList: { ...list.sectionsList, name: finalName } }
            : list
        ));
      }
      setIsEditingListName(false);
      setEditingListName('');
      setHasUnsavedChanges(true);
      
      // Trigger change detection
      setTimeout(() => checkForChanges(), 0);
    }
  };

  const handleCancelEditListName = () => {
    setIsEditingListName(false);
    setEditingListName('');
  };

  const canDeleteList = (list: ListItem): boolean => {
    return lists.some((candidate) => candidate.id !== list.id);
  };

  const getFallbackListId = (list: ListItem): number | null => {
    return selectSectionsListFallbackId(lists, Number(list.sectionsList.id));
  };

  const handleDeleteList = async (list: ListItem) => {
    setError(null);
    const usedInApps = listAppCounts[list.id] || 0;

    if (usedInApps > 0) {
      // Show modal warning if list is used in apps
      setListToDelete(list);
      setIsDeleteModalOpen(true);
    } else {
      const confirmed = window.confirm(`Ви дійсно хочете видалити список "${list.name}"?`);
      if (!confirmed) return;
      await performDeleteList(list);
    }
  };

  const performDeleteList = async (list: ListItem): Promise<boolean> => {
    const deletedListId = Number(list.sectionsList.id);
    setDeletingListId(deletedListId);
    setError(null);

    try {
      const result = await PanelService.deleteSectionsList(deletedListId);
      const fallbackListId = Number(result.fallbackList?.id) || null;
      const affectedAppIds = Array.isArray(result.linkedAppIds)
        ? result.linkedAppIds.map(Number)
        : (listAppIds[deletedListId] || []);

      setLists((previous) => previous.filter(
        (candidate) => Number(candidate.sectionsList.id) !== deletedListId
      ));
      setApps((previous) => applySectionsListFallbackToApps(
        previous,
        deletedListId,
        fallbackListId,
        affectedAppIds
      ));
      setListAppIds((previous) => {
        const next = { ...previous };
        delete next[deletedListId];
        if (fallbackListId) {
          next[fallbackListId] = Array.from(new Set([
            ...(next[fallbackListId] || []),
            ...affectedAppIds,
          ])).sort((left, right) => left - right);
        }
        return next;
      });
      setListAppCounts((previous) => {
        const next = { ...previous };
        delete next[deletedListId];
        if (fallbackListId) {
          next[fallbackListId] = (next[fallbackListId] || 0) + affectedAppIds.length;
        }
        return next;
      });

      if (selectedListId === list.id) {
        setSelectedListId(null);
        setIsExpanded(false);
      }
      return true;
    } catch (deleteError: any) {
      const responseData = deleteError?.response?.data;
      const message = responseData?.error
        || responseData?.message
        || (typeof responseData === 'string' ? responseData : '')
        || deleteError?.message
        || 'Не вдалося видалити список.';
      logger.error('Error deleting sections list', deleteError);
      setError(String(message));
      return false;
    } finally {
      setDeletingListId(null);
    }
  };

  const handleDeleteListConfirm = async () => {
    if (listToDelete) {
      const deleted = await performDeleteList(listToDelete);
      if (!deleted) return;
    }
    setIsDeleteModalOpen(false);
    setListToDelete(null);
  };

  const handleDeleteListCancelModal = () => {
    setIsDeleteModalOpen(false);
    setListToDelete(null);
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  const getNextCopyName = (baseName: string): string => {
    const escapedBase = baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const copyRegex = new RegExp(`^${escapedBase} Copy #(\\d+)$`);
    const usedNumbers = lists
      .map((l) => {
        const m = l.name.match(copyRegex);
        return m ? parseInt(m[1], 10) : null;
      })
      .filter((n): n is number => n != null);
    const next = usedNumbers.length ? Math.max(...usedNumbers) + 1 : 1;
    return `${baseName} Copy #${next}`;
  };

  const handleDuplicateList = async (list: ListItem) => {
    const confirmed = window.confirm(`Ви дійсно хочете дублювати список "${list.name}"?`);
    if (!confirmed) return;

    try {
      const newListId = Math.max(...lists.map((l) => l.id), 0) + 1;
      const newSectionsListId = Math.max(...lists.map((l) => l.sectionsList.id), 0) + 1;
      const duplicatedName = getNextCopyName(list.name);
      const cloned = JSON.parse(JSON.stringify(list.sectionsList));
      delete cloned._id;
      const duplicatedSectionsList: ISectionsList = {
        ...cloned,
        id: newSectionsListId,
        name: duplicatedName,
      };

      await PanelService.upsertSectionsList(duplicatedSectionsList);

      setLists((prev) => [
        ...prev,
        {
          id: newListId,
          name: duplicatedName,
          sectionsList: duplicatedSectionsList,
        },
      ]);
    } catch (error) {
      logger.error('Error duplicating list', error);
    }
  };

  const resetDeleteState = () => {
    setIsHoldingDelete(null);
    setHoldProgress({});
    setIsRemoving(null);
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const handleSaveList = async () => {
    if (!selectedListId) return;
    
    try {
      setIsSaving(true);
      setSaveError(null);
      const selectedList = lists.find(list => list.id === selectedListId);
      const currentList = selectedList || newListData;
      
      if (!currentList) {
        logger.error('Selected list not found');
        return;
      }
      
      // If currently editing the name, save the editing name
      let finalName = currentList.name;
      if (isEditingListName && editingListName.trim()) {
        finalName = editingListName.trim();
        // Exit edit mode
        setIsEditingListName(false);
        setEditingListName('');
      }

      const updatedSectionsList = buildSectionsListSavePayload(sectionsList, finalName);

      const savedSectionsList = await PanelService.upsertSectionsList(updatedSectionsList);
      const persistedSectionsList = {
        ...updatedSectionsList,
        ...savedSectionsList,
        name: finalName,
        offerwallType: PDL_OFFERWALL_TYPE
      };
      
      // If this was a new list, add it to the main lists state
      if (isCreatingNewList && newListData) {
        setLists(prev => [...prev, {
          ...newListData,
          id: persistedSectionsList.id,
          name: finalName,
          sectionsList: persistedSectionsList
        }]);
        setSelectedListId(persistedSectionsList.id);
        setIsCreatingNewList(false);
        setNewListData(null);
      } else {
        // Update existing list
        setLists(prev => prev.map(list => 
          list.id === selectedListId
            ? { ...list, id: persistedSectionsList.id, sectionsList: persistedSectionsList, name: finalName }
            : list
        ));
      }

      setSectionsList(persistedSectionsList);
      
      // Reset change tracking after successful save
      setOriginalSectionsList(JSON.parse(JSON.stringify(persistedSectionsList)));
      setOriginalListName(finalName);
      setHasUnsavedChanges(false);
      
      logger.info('List saved successfully');
    } catch (error) {
      logger.error('Error saving list', error);
      setSaveError('Не вдалося зберегти список. Спробуйте ще раз.');
    } finally {
      setIsSaving(false);
    }
  };

  // Filter lists by search query
  const filteredLists = lists.filter(list => 
    list.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    return () => {
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const particlesInit = useCallback(async (engine: Engine) => {
    await loadFull(engine);
  }, []);

  const particlesLoaded = useCallback(async (container: Container | undefined) => {
    if (container) {
      logger.debug('Particles container loaded');
    }
  }, []);

  // Render lists overview
  if (!isExpanded) {
    return (
      <div className="offers-container">
        <Particles
          id="tsparticles"
          init={particlesInit}
          loaded={particlesLoaded}
          options={{
            fullScreen: false,
            background: {
              color: {
                value: "#000000",
              },
            },
            fpsLimit: 60,
            interactivity: {
              events: {
                onHover: {
                  enable: true,
                  mode: ["trail", "repulse"],
                },
                resize: true,
              },
              modes: {
                trail: {
                  delay: 0.05,
                  pauseOnStop: true,
                  quantity: 2,
                  particles: {
                    size: {
                      value: { min: 2, max: 4 }
                    },
                    move: {
                      speed: 1.5,
                      outModes: {
                        default: "none"
                      }
                    },
                    opacity: {
                      value: 0.3,
                      animation: {
                        enable: true,
                        speed: 0.2,
                        minimumValue: 0,
                        sync: false,
                        destroy: "min"
                      }
                    }
                  }
                },
                repulse: {
                  distance: 100,
                  duration: 0.2,
                  factor: 10,
                  speed: 0.3,
                  maxSpeed: 1,
                  easing: "ease-out-cubic"
                }
              },
            },
            particles: {
              color: {
                value: "#169de0",
              },
              links: {
                enable: false,
              },
              move: {
                direction: "none",
                enable: true,
                outModes: {
                  default: "none",
                },
                random: true,
                speed: 0.2,
                straight: false,
              },
              number: {
                density: {
                  enable: true,
                  area: 800,
                },
                value: 100,
                limit: 200,
              },
              opacity: {
                value: 0.3,
                animation: {
                  enable: true,
                  speed: 0.2,
                  minimumValue: 0,
                  sync: false,
                  destroy: "min"
                }
              },
              shape: {
                type: "circle",
              },
              size: {
                value: { min: 2, max: 4 }
              },
              effect: {
                type: "star",
                options: {
                  star: {
                    blur: 3,
                    color: "#169de0",
                    frequency: 0.1,
                    opacity: 0.15,
                    size: 2
                  }
                }
              }
            },
            detectRetina: true,
          }}
        />
        <div className="lists-container">
          <div className="lists-header">
            <div className="lists-header-content">
              <div className="lists-tabs">
                <button 
                  className={`lists-tab ${activeTab === 'offerwall' ? 'active' : ''}`}
                  onClick={() => setActiveTab('offerwall')}
                >
                  Офервол
                </button>
                <button 
                  className={`lists-tab ${activeTab === 'direct' ? 'active' : ''}`}
                  onClick={() => setActiveTab('direct')}
                >
                  Прямі кампанії
                </button>
                {/* Sections tab hidden for now */}
              </div>
              <div className="lists-search" style={{ display: activeTab === 'sections' ? 'none' : 'flex' }}>
                <input
                  type="text"
                  placeholder="Пошук"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="lists-search-input"
                />
                <FontAwesomeIcon icon={faSearch} className="lists-search-icon" />
              </div>
            </div>
          </div>
          <div className="lists-content">
            {error && <div className="lists-error" role="alert">{error}</div>}
            {activeTab === 'offerwall' ? (
              <>
                <div className="lists-grid">
                  {filteredLists.map((list) => (
                    <div 
                      key={list.id}
                      className="list-card"
                      onClick={() => handleSelectList(list.id)}
                    >
                      <div className="list-card-content">
                        <div className="list-card-header">
                          <h3>{list.name}</h3>
                        </div>
                        <div className="list-card-info">
                          <Whisper
                            placement="topEnd"
                            trigger="hover"
                            speaker={
                              <Popover>
                                {listAppIds[list.id]?.length ? (
                                  <div style={{ maxWidth: '250px', wordWrap: 'break-word', fontSize: '12px' }}>
                                    {listAppIds[list.id].map(id => `#${id}`).join(', ')}
                                  </div>
                                ) : (
                                  <span style={{ fontSize: '12px' }}>Не використовується</span>
                                )}
                              </Popover>
                            }
                          >
                            {(listAppCounts[list.id] || 0) > 0 && (
                              <span 
                                className="list-card-apps" 
                                style={{ textAlign: 'right', display: 'block', cursor: 'help' }}
                              >
                                Використовується в застосунках: <strong>{listAppCounts[list.id]}</strong>
                              </span>
                            )}
                          </Whisper>
                        </div>
                        <button
                          className="list-card-duplicate"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateList(list);
                          }}
                          title="Дублювати список"
                        >
                          <FontAwesomeIcon icon={faCopy} className="duplicate-icon" />
                        </button>
                        {canDeleteList(list) && (
                          <button 
                            className="list-card-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteList(list);
                            }}
                          >
                            <FontAwesomeIcon 
                              icon={faTrash} 
                              className="trash-icon"
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="lists-footer">
                  <Button 
                    appearance="primary"
                    onClick={handleCreateNewList}
                    size="lg"
                  >
                    Новий список
                  </Button>
                </div>
              </>
            ) : activeTab === 'direct' ? (
              <div className="direct-companies-placeholder">
                <p>Тут будуть прямі кампанії</p>
              </div>
            ) : null}
            {activeTab === 'sections' && (
              <SectionsManager />
            )}
          </div>
        </div>
        
        {/* Delete Confirmation Modal — shown only when list is used in apps */}
        {isDeleteModalOpen && listToDelete && (
          <div className="delete-modal-overlay">
            <div className="delete-modal">
              <h3>Видалити список?</h3>
              <p>Ви дійсно хочете видалити список "{listToDelete.name}"?</p>
              <div className="delete-warning">
                <p>⚠️ Увага: Цей список використовується в <strong>{listAppCounts[listToDelete.id] || 0}</strong> застосунках.</p>
                {(() => {
                  const fallbackId = getFallbackListId(listToDelete);
                  const fallbackList = lists.find(l => l.sectionsList.id === fallbackId);
                  return fallbackList ? (
                    <p style={{ marginTop: '6px' }}>Застосунки будуть переведені на список: <strong>{fallbackList.name}</strong></p>
                  ) : null;
                })()}
              </div>
              {error && <div className="lists-error delete-modal-error" role="alert">{error}</div>}
              <div className="delete-modal-buttons">
                <Button 
                  appearance="ghost"
                  onClick={handleDeleteListCancelModal}
                  disabled={deletingListId !== null}
                >
                  Скасувати
                </Button>
                <Button 
                  appearance="primary"
                  color="red"
                  onClick={handleDeleteListConfirm}
                  loading={deletingListId === Number(listToDelete.sectionsList.id)}
                  disabled={deletingListId !== null}
                >
                  Видалити
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render expanded offers list
  const selectedList = lists.find(list => list.id === selectedListId) || newListData;

  return (
    <div className="offers-container">
      <Particles
        id="tsparticles"
        init={particlesInit}
        loaded={particlesLoaded}
        options={{
          fullScreen: false,
          background: {
            color: {
              value: "#000000",
            },
          },
          fpsLimit: 60,
          interactivity: {
            events: {
              onHover: {
                enable: true,
                mode: ["trail", "repulse"],
              },
              resize: true,
            },
            modes: {
              trail: {
                delay: 0.05,
                pauseOnStop: true,
                quantity: 2,
                particles: {
                  size: {
                    value: { min: 2, max: 4 }
                  },
                  move: {
                    speed: 1.5,
                    outModes: {
                      default: "none"
                    }
                  },
                  opacity: {
                    value: 0.3,
                    animation: {
                      enable: true,
                      speed: 0.2,
                      minimumValue: 0,
                      sync: false,
                      destroy: "min"
                    }
                  }
                }
              },
              repulse: {
                distance: 100,
                duration: 0.2,
                factor: 10,
                speed: 0.3,
                maxSpeed: 1,
                easing: "ease-out-cubic"
              }
            },
          },
          particles: {
            color: {
              value: "#169de0",
            },
            links: {
              enable: false,
            },
            move: {
              direction: "none",
              enable: true,
              outModes: {
                default: "none",
              },
              random: true,
              speed: 0.2,
              straight: false,
            },
            number: {
              density: {
                enable: true,
                area: 800,
              },
              value: 100,
              limit: 200,
            },
            opacity: {
              value: 0.3,
              animation: {
                enable: true,
                speed: 0.2,
                minimumValue: 0,
                sync: false,
                destroy: "min"
              }
            },
            shape: {
              type: "circle",
            },
            size: {
              value: { min: 2, max: 4 }
            },
            effect: {
              type: "star",
              options: {
                star: {
                  blur: 3,
                  color: "#169de0",
                  frequency: 0.1,
                  opacity: 0.15,
                  size: 2
                }
              }
            }
          },
          detectRetina: true,
        }}
      />
      <div className="list-header">
        <div className="list-header-content">
          <Button 
            appearance="ghost"
            onClick={handleBackToList}
            style={{ marginRight: '16px' }}
          >
            ← Назад до списків
          </Button>
          <div className="list-header-info">
            {isEditingListName ? (
              <div className="list-name-edit">
                <Input
                  value={editingListName}
                  onChange={setEditingListName}
                  onPressEnter={handleSaveListName}
                  onBlur={handleSaveListName}
                  autoFocus
                  size="sm"
                  style={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(22, 157, 224, 0.5)',
                    color: '#ffffff',
                    fontSize: '18px',
                    fontWeight: '600',
                    minWidth: '200px'
                  }}
                />
                <Button 
                  appearance="ghost" 
                  size="xs"
                  onClick={handleCancelEditListName}
                  style={{ marginLeft: '8px', color: 'rgba(255, 255, 255, 0.6)' }}
                >
                  ✕
                </Button>
              </div>
            ) : (
              <h2 
                onClick={handleEditListName}
                style={{ cursor: 'pointer' }}
                title="Натисніть для редагування"
              >
                {selectedList?.name || 'Невідомий список'}
              </h2>
            )}
          </div>
          <div className="list-header-actions" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
            {saveError && (
              <span role="alert" style={{ color: '#ff6b6b', fontSize: '13px' }}>
                {saveError}
              </span>
            )}
            <Button 
              appearance="subtle"
              onClick={() => {
                // Set default geo to first available if current is not in list
                if (!availableGeos.has(previewGeo) && availableGeos.size > 0) {
                  setPreviewGeo(Array.from(availableGeos)[0]);
                }
                setIsPreviewModalOpen(true);
              }}
              disabled={availableGeos.size === 0}
              style={{
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: availableGeos.size === 0 ? 0.5 : 1
              }}
              title={availableGeos.size === 0 ? "Немає офферів для відображення" : "Попередній перегляд офервола"}
            >
              <FontAwesomeIcon icon={faMobileAlt} style={{ fontSize: '18px' }} />
            </Button>
            <Button 
              appearance="primary"
              color="blue"
              loading={isSaving}
              disabled={!hasUnsavedChanges}
              onClick={handleSaveList}
              style={{
                opacity: hasUnsavedChanges ? 1 : 0.6,
                cursor: hasUnsavedChanges ? 'pointer' : 'not-allowed'
              }}
            >
              {isSaving ? 'Збереження...' : 'Зберегти список'}
            </Button>
          </div>
        </div>
      </div>
      <div className="drag-drop-context">
        <DragDropContext 
          onDragEnd={handleDragEnd}
          enableDefaultSensors={true}
          dragHandleUsageInstructions=""
        >
          <SplitLayout
            leftPane={
              <div className="offers-drop-zone">
                <OffersList 
                  droppableId="offers-list" 
                  sectionsList={sectionsList}
                  allOffers={selectedOffers.reduce((acc, offer) => {
                    acc[offer.id] = offer;
                    return acc;
                  }, {} as Record<number, TOffer>)}
                  partners={partners}
                  sectionTitles={sectionTitles}
                  defaultExpandedSection={expandedSection}
                  onSectionExpand={handleSectionExpand}
                  onSectionsListChange={handleSectionsListChange}
                  draggedOffer={draggedOffer}
                  onAddOffer={handleAddOffer}
                  onEditOffer={handleEditOffer}
                  isReorderMode={isReorderMode}
                  lastExpandedSection={lastExpandedSection}
                  onExitReorderMode={() => setIsReorderMode(false)}
                />
                <div className="list-buttons-container">
                  <Button 
                    appearance="ghost"
                    color={isReorderMode ? "green" : "yellow"}
                    style={{
                      fontWeight: isReorderMode ? 'bold' : 'normal'
                    }}
                    onClick={toggleReorderMode}
                  >
                    {isReorderMode ? 'Зберегти' : 'Змінити порядок'}
                  </Button>
                </div>
              </div>
            }
            rightPane={
              <AllOffersList 
                droppableId="all-offers-list" 
                sectionsList={sectionsList}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                getOfferHiddenState={getOfferHiddenState}
              />
            }
          />
        </DragDropContext>
      </div>

      {isPreviewModalOpen && (
        <div 
          className={`offerwall-preview-overlay ${isPreviewModalClosing ? 'closing' : ''}`}
          onClick={closePreviewModal}
          onKeyDown={(e) => e.key === 'Escape' && closePreviewModal()}
          tabIndex={0}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.25)',
            backdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '40px',
            animation: isPreviewModalClosing ? 'fadeOut 0.2s ease-out forwards' : 'fadeIn 0.2s ease-out forwards'
          }}
        >
          <div 
            className="offerwall-preview-phone"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '375px',
              height: 'calc(100vh - 80px)',
              maxHeight: '812px',
              border: '10px solid #1a1a1a',
              borderRadius: '44px',
              overflow: 'hidden',
              background: '#000',
              position: 'relative',
              boxShadow: '0 25px 80px -12px rgba(0, 0, 0, 0.8), inset 0 0 0 2px #333'
            }}
          >
            {/* Phone navbar with pickers */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '48px',
              background: '#1a1a1a',
              zIndex: 10,
              display: 'flex',
              alignItems: 'stretch'
            }}>
              {/* Geo picker - left half */}
              <div style={{ flex: 1, display: 'flex' }}>
                <SelectPicker
                  data={geoPickerData}
                  value={previewGeo}
                  onChange={(value) => setPreviewGeo(value || 'UA')}
                  searchable
                  locale={{ searchPlaceholder: 'Пошук' }}
                  cleanable={false}
                  placement="bottomStart"
                  appearance="subtle"
                  style={{ width: '100%' }}
                  menuStyle={{ zIndex: 10001 }}
                  toggleAs={({ ...props }) => (
                    <div {...props} style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                      padding: '0 4px',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      const content = e.currentTarget.querySelector('.picker-content') as HTMLElement;
                      if (content) {
                        content.style.transform = 'scale(1.03)';
                        content.style.color = '#3498ff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const content = e.currentTarget.querySelector('.picker-content') as HTMLElement;
                      if (content) {
                        content.style.transform = 'scale(1)';
                        content.style.color = '#fff';
                      }
                    }}
                    >
                      <span style={{ fontSize: '18px', color: '#444', paddingLeft: '16px' }}>▾</span>
                      <span className="picker-content" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'transform 0.15s ease, color 0.15s ease', color: '#fff' }}>
                        <span style={{ fontSize: '20px' }}>{getCountryFlag(previewGeo)}</span>
                        <span style={{ fontWeight: 600, fontSize: '14px' }}>{previewGeo}</span>
                      </span>
                    </div>
                  )}
                  renderMenuItem={(label, item) => item.label}
                />
              </div>
              
              {/* Central notch with close button */}
              <div 
                onClick={closePreviewModal}
                style={{
                  width: '100px',
                  height: '48px',
                  background: '#000',
                  borderRadius: '0 0 16px 16px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
                title="Закрити"
                onMouseEnter={(e) => {
                  const cross = e.currentTarget.querySelector('.preview-close-cross') as HTMLElement;
                  if (cross) cross.style.transform = 'scale(1.3)';
                }}
                onMouseLeave={(e) => {
                  const cross = e.currentTarget.querySelector('.preview-close-cross') as HTMLElement;
                  if (cross) cross.style.transform = 'scale(1)';
                }}
              >
                <span className="preview-close-cross" style={{ color: '#555', fontSize: '16px', fontWeight: 300, transition: 'transform 0.15s ease' }}>✕</span>
              </div>
              
              <div style={{ flex: 1 }} />
            </div>
            
            {isLoadingPreview ? (
              <div style={{
                width: '100%',
                height: 'calc(100% - 48px)',
                marginTop: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#888'
              }}>
                Завантаження...
              </div>
            ) : previewError ? (
              <div style={{
                width: '100%',
                height: 'calc(100% - 48px)',
                marginTop: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
                color: '#ff8f8f',
                textAlign: 'center',
                lineHeight: 1.45,
                background: '#050505'
              }}>
                {previewError}
              </div>
            ) : (
              <iframe
                ref={previewIframeRef}
                srcDoc={previewHtml}
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
                style={{
                  width: '100%',
                  height: 'calc(100% - 48px)',
                  border: 'none',
                  marginTop: '48px'
                }}
                title="Offerwall Preview"
              />
            )}
          </div>
        </div>
      )}

      {/* Offer Edit Modal */}
      {editingOffer && (
        <OfferCrudModal 
          offer={editingOffer}
          offers={selectedOffers}
          partners={Object.values(partners)}
          onClose={() => setEditingOffer(null)}
          onSave={handleOfferSaved}
        />
      )}
    </div>
  );
};

export default OfferListContainer; 
